import { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Home, User, Calendar, MessageCircle, LogOut } from 'lucide-react';
import Messages from '@/components/Messages';
import { toast } from 'sonner';
import { categories } from '@/utils/categories';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

const ArtistDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [profile, setProfile] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profileForm, setProfileForm] = useState({
    category: '',
    subcategory: '',
    bio: '',
    experience_years: '',
    location: '',
    price_starting: '',
    youtube_link: '',
    instagram_handle: '',
    facebook_link: '',
    portfolio_images: [],
    skills: [],
  });
  const [skillInput, setSkillInput] = useState('');
  const [imageUrlInput, setImageUrlInput] = useState('');

  useEffect(() => {
    fetchProfile();
    fetchBookings();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/artists?category=`);
      const userProfile = response.data.find((a) => a.user_id === user.id);
      if (userProfile) {
        setProfile(userProfile);
        setProfileForm({
          category: userProfile.category,
          subcategory: userProfile.subcategory || '',
          bio: userProfile.bio,
          experience_years: userProfile.experience_years,
          location: userProfile.location,
          price_starting: userProfile.price_starting,
          youtube_link: userProfile.youtube_link || '',
          instagram_handle: userProfile.instagram_handle || '',
          facebook_link: userProfile.facebook_link || '',
          portfolio_images: userProfile.portfolio_images || [],
          skills: userProfile.skills || [],
        });
      }
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/bookings`);
      setBookings(response.data);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...profileForm,
        experience_years: parseInt(profileForm.experience_years),
        price_starting: parseFloat(profileForm.price_starting),
      };

      if (profile) {
        await axios.put(`${API}/artists/${profile.id}`, data);
        toast.success('Profile updated successfully');
      } else {
        await axios.post(`${API}/artists`, data);
        toast.success('Profile created successfully');
      }
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save profile');
    }
  };

  const handleBookingStatus = async (bookingId, status) => {
    try {
      await axios.put(`${API}/bookings/${bookingId}?status=${status}`);
      toast.success(`Booking ${status}`);
      fetchBookings();
    } catch (error) {
      toast.error('Failed to update booking');
    }
  };

  const addSkill = () => {
    if (skillInput.trim()) {
      setProfileForm({
        ...profileForm,
        skills: [...profileForm.skills, skillInput.trim()],
      });
      setSkillInput('');
    }
  };

  const removeSkill = (index) => {
    setProfileForm({
      ...profileForm,
      skills: profileForm.skills.filter((_, i) => i !== index),
    });
  };

  const addImage = () => {
    if (imageUrlInput.trim()) {
      setProfileForm({
        ...profileForm,
        portfolio_images: [...profileForm.portfolio_images, imageUrlInput.trim()],
      });
      setImageUrlInput('');
    }
  };

  const removeImage = (index) => {
    setProfileForm({
      ...profileForm,
      portfolio_images: profileForm.portfolio_images.filter((_, i) => i !== index),
    });
  };

  const isActive = (path) => location.pathname.includes(path);

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="artist-dashboard">
      <div className="flex">
        <aside className="w-64 bg-white border-r min-h-screen p-6">
          <div className="mb-8">
            <Link to="/">
              <div className="text-2xl font-bold gradient-text">BOOK A STAR</div>
            </Link>
            <p className="text-sm text-gray-600 mt-2">Artist Dashboard</p>
          </div>

          <nav className="space-y-2">
            <Link to="/dashboard/artist" data-testid="nav-overview">
              <Button
                variant={
                  isActive('/dashboard/artist') && !isActive('/profile') && !isActive('/messages')
                    ? 'secondary'
                    : 'ghost'
                }
                className="w-full justify-start"
              >
                <Home className="w-4 h-4 mr-2" />
                Overview
              </Button>
            </Link>
            <Link to="/dashboard/artist/profile" data-testid="nav-profile">
              <Button
                variant={isActive('/profile') ? 'secondary' : 'ghost'}
                className="w-full justify-start"
              >
                <User className="w-4 h-4 mr-2" />
                My Profile
              </Button>
            </Link>
            <Link to="/dashboard/artist/bookings" data-testid="nav-bookings">
              <Button
                variant={isActive('/bookings') ? 'secondary' : 'ghost'}
                className="w-full justify-start"
              >
                <Calendar className="w-4 h-4 mr-2" />
                Bookings
              </Button>
            </Link>
            <Link to="/dashboard/artist/messages" data-testid="nav-messages">
              <Button
                variant={isActive('/messages') ? 'secondary' : 'ghost'}
                className="w-full justify-start"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Messages
              </Button>
            </Link>
          </nav>

          <div className="mt-auto pt-6 border-t">
            <Button
              variant="ghost"
              className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={handleLogout}
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </aside>

        <main className="flex-1 p-8">
          <Routes>
            <Route
              path="/"
              element={
                <div data-testid="overview-content">
                  <h1 className="text-3xl font-semibold mb-6">Welcome, {user?.name}!</h1>

                  {!profile && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 mb-6">
                      <p className="text-yellow-800 font-semibold mb-2">
                        Complete your profile to start receiving bookings!
                      </p>
                      <Link to="/dashboard/artist/profile">
                        <Button size="sm">Create Profile</Button>
                      </Link>
                    </div>
                  )}

                  <div className="grid md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-white rounded-xl p-6 border border-gray-100">
                      <div className="text-sm text-gray-600 mb-2">Total Bookings</div>
                      <div className="text-3xl font-bold">{profile?.bookings_count || 0}</div>
                    </div>
                    <div className="bg-white rounded-xl p-6 border border-gray-100">
                      <div className="text-sm text-gray-600 mb-2">Rating</div>
                      <div className="text-3xl font-bold">{profile?.rating || 0}</div>
                    </div>
                    <div className="bg-white rounded-xl p-6 border border-gray-100">
                      <div className="text-sm text-gray-600 mb-2">Reviews</div>
                      <div className="text-3xl font-bold">{profile?.reviews_count || 0}</div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl border border-gray-100 p-6">
                    <h2 className="text-xl font-semibold mb-4">Recent Bookings</h2>
                    {bookings.length === 0 ? (
                      <p className="text-gray-600">No bookings yet</p>
                    ) : (
                      <div className="space-y-4">
                        {bookings.slice(0, 5).map((booking) => (
                          <div
                            key={booking.id}
                            className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                          >
                            <div>
                              <div className="font-semibold">{booking.customer_name}</div>
                              <div className="text-sm text-gray-600">
                                {booking.event_type} • {booking.event_date}
                              </div>
                            </div>
                            <Badge className={getStatusColor(booking.status)}>
                              {booking.status}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              }
            />
            <Route
              path="/profile"
              element={
                <div data-testid="profile-content">
                  <h1 className="text-3xl font-semibold mb-6">
                    {profile ? 'Edit Profile' : 'Create Profile'}
                  </h1>

                  <div className="bg-white rounded-xl border border-gray-100 p-8">
                    <form onSubmit={handleProfileSubmit} className="space-y-6">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="category">Category *</Label>
                          <Select
                            value={profileForm.category}
                            onValueChange={(value) =>
                              setProfileForm({ ...profileForm, category: value, subcategory: '' })
                            }
                            required
                          >
                            <SelectTrigger data-testid="category-select">
                              <SelectValue placeholder="Select category" />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.values(categories).map((cat) => (
                                <SelectItem key={cat.value} value={cat.value}>
                                  {cat.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {profileForm.category &&
                          categories[profileForm.category]?.subcategories.length > 0 && (
                            <div className="space-y-2">
                              <Label htmlFor="subcategory">Subcategory</Label>
                              <Select
                                value={profileForm.subcategory}
                                onValueChange={(value) =>
                                  setProfileForm({ ...profileForm, subcategory: value })
                                }
                              >
                                <SelectTrigger data-testid="subcategory-select">
                                  <SelectValue placeholder="Select subcategory" />
                                </SelectTrigger>
                                <SelectContent>
                                  {categories[profileForm.category].subcategories.map((sub) => (
                                    <SelectItem key={sub.value} value={sub.value}>
                                      {sub.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="bio">Bio *</Label>
                        <Textarea
                          id="bio"
                          value={profileForm.bio}
                          onChange={(e) => setProfileForm({ ...profileForm, bio: e.target.value })}
                          placeholder="Tell us about yourself and your work..."
                          rows={4}
                          required
                          data-testid="bio-input"
                        />
                      </div>

                      <div className="grid md:grid-cols-3 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="experience_years">Experience (years) *</Label>
                          <Input
                            id="experience_years"
                            type="number"
                            value={profileForm.experience_years}
                            onChange={(e) =>
                              setProfileForm({ ...profileForm, experience_years: e.target.value })
                            }
                            required
                            data-testid="experience-input"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="location">Location *</Label>
                          <Input
                            id="location"
                            value={profileForm.location}
                            onChange={(e) =>
                              setProfileForm({ ...profileForm, location: e.target.value })
                            }
                            placeholder="City, State"
                            required
                            data-testid="location-input"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="price_starting">Starting Price ($) *</Label>
                          <Input
                            id="price_starting"
                            type="number"
                            value={profileForm.price_starting}
                            onChange={(e) =>
                              setProfileForm({ ...profileForm, price_starting: e.target.value })
                            }
                            required
                            data-testid="price-input"
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-3 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="instagram_handle">Instagram Handle</Label>
                          <Input
                            id="instagram_handle"
                            value={profileForm.instagram_handle}
                            onChange={(e) =>
                              setProfileForm({ ...profileForm, instagram_handle: e.target.value })
                            }
                            placeholder="username"
                            data-testid="instagram-input"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="youtube_link">YouTube Channel</Label>
                          <Input
                            id="youtube_link"
                            value={profileForm.youtube_link}
                            onChange={(e) =>
                              setProfileForm({ ...profileForm, youtube_link: e.target.value })
                            }
                            placeholder="https://youtube.com/..."
                            data-testid="youtube-input"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="facebook_link">Facebook Profile</Label>
                          <Input
                            id="facebook_link"
                            value={profileForm.facebook_link}
                            onChange={(e) =>
                              setProfileForm({ ...profileForm, facebook_link: e.target.value })
                            }
                            placeholder="https://facebook.com/..."
                            data-testid="facebook-input"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Skills</Label>
                        <div className="flex gap-2">
                          <Input
                            value={skillInput}
                            onChange={(e) => setSkillInput(e.target.value)}
                            placeholder="Add a skill"
                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                            data-testid="skill-input"
                          />
                          <Button type="button" onClick={addSkill} data-testid="add-skill-button">
                            Add
                          </Button>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {profileForm.skills.map((skill, idx) => (
                            <Badge key={idx} variant="secondary" className="px-3 py-1">
                              {skill}
                              <button
                                type="button"
                                onClick={() => removeSkill(idx)}
                                className="ml-2 text-gray-500 hover:text-gray-700"
                              >
                                ×
                              </button>
                            </Badge>
                          ))}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Portfolio Images</Label>
                        <div className="flex gap-2">
                          <Input
                            value={imageUrlInput}
                            onChange={(e) => setImageUrlInput(e.target.value)}
                            placeholder="Add image URL"
                            data-testid="image-url-input"
                          />
                          <Button type="button" onClick={addImage} data-testid="add-image-button">
                            Add
                          </Button>
                        </div>
                        {profileForm.portfolio_images.length > 0 && (
                          <div className="grid grid-cols-4 gap-4 mt-4">
                            {profileForm.portfolio_images.map((img, idx) => (
                              <div key={idx} className="relative group">
                                <img
                                  src={img}
                                  alt={`Portfolio ${idx + 1}`}
                                  className="w-full h-24 object-cover rounded-lg"
                                />
                                <button
                                  type="button"
                                  onClick={() => removeImage(idx)}
                                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <Button
                        type="submit"
                        className="bg-purple-600 hover:bg-purple-700"
                        data-testid="save-profile-button"
                      >
                        {profile ? 'Update Profile' : 'Create Profile'}
                      </Button>
                    </form>
                  </div>
                </div>
              }
            />
            <Route
              path="/bookings"
              element={
                <div data-testid="bookings-content">
                  <h1 className="text-3xl font-semibold mb-6">Bookings</h1>

                  {bookings.length === 0 ? (
                    <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
                      <p className="text-gray-600">No bookings yet</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {bookings.map((booking) => (
                        <div
                          key={booking.id}
                          className="bg-white rounded-xl border border-gray-100 p-6"
                          data-testid={`booking-${booking.id}`}
                        >
                          <div className="flex items-start justify-between mb-4">
                            <div>
                              <h3 className="text-xl font-semibold">{booking.customer_name}</h3>
                              <p className="text-gray-600">{booking.customer_email}</p>
                            </div>
                            <Badge className={getStatusColor(booking.status)}>
                              {booking.status}
                            </Badge>
                          </div>
                          <div className="grid md:grid-cols-2 gap-4 text-sm mb-4">
                            <div>
                              <span className="text-gray-600">Event:</span> {booking.event_type}
                            </div>
                            <div>
                              <span className="text-gray-600">Date:</span> {booking.event_date}
                            </div>
                            <div>
                              <span className="text-gray-600">Location:</span>{' '}
                              {booking.event_location}
                            </div>
                            <div>
                              <span className="text-gray-600">Guests:</span>{' '}
                              {booking.guest_count || 'N/A'}
                            </div>
                          </div>
                          {booking.special_requests && (
                            <div className="mb-4 text-sm">
                              <span className="text-gray-600">Special Requests:</span>{' '}
                              {booking.special_requests}
                            </div>
                          )}
                          {booking.status === 'pending' && (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => handleBookingStatus(booking.id, 'confirmed')}
                                data-testid={`accept-booking-${booking.id}`}
                              >
                                Accept
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleBookingStatus(booking.id, 'rejected')}
                                data-testid={`reject-booking-${booking.id}`}
                              >
                                Reject
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              }
            />
            <Route path="/messages" element={<Messages />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default ArtistDashboard;
